import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, TrendingUp, Users, BarChart3, Coins, FileSignature, Bell, Wallet, Package, CheckCircle, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import InstallAppBanner from '@/components/InstallAppBanner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, trend, color = 'blue', testId }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-inter text-slate-600">{title}</p>
        <h3 className="text-3xl font-jakarta font-bold text-slate-900 mt-2">{value}</h3>
        {trend && <p className="text-sm text-slate-500 mt-1">{trend}</p>}
      </div>
      <div className={`p-3 bg-${color}-100 rounded-lg`}>
        <Icon className={`text-${color}-600`} size={24} />
      </div>
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-slate-200">
        <p className="font-semibold text-slate-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: ₱{entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [vendoSummary, setVendoSummary] = useState(null);
  const [vendoMachinesCount, setVendoMachinesCount] = useState(0);
  const [unsignedPayslips, setUnsignedPayslips] = useState({ count: 0, payslips: [] });
  const [myCashAdvance, setMyCashAdvance] = useState({ outstanding: 0, pending: 0, advances: [] });
  const [pendingNASOrders, setPendingNASOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Users who can see the full dashboard
  const dashboardAccessUsers = ['alma', 'jelyn', 'nicole', 'carla', 'kristine', 'pabs', 'arnold'];
  
  // Users who can approve NAS orders
  const nasOrderApprovers = ['arbe', 'alma', 'pabs', 'kristine', 'jelyn', 'carla', 'arnold', 'nicole'];
  
  const usernameLower = user?.username?.toLowerCase() || '';
  const fullNameLower = user?.full_name?.toLowerCase().replace(/\s+/g, '') || '';
  
  const hasDashboardAccess = user?.role === 'admin' || 
    dashboardAccessUsers.some(name => usernameLower.includes(name) || fullNameLower.includes(name));
  
  // Check if user can approve NAS orders
  const canApproveNASOrders = user?.role === 'admin' || 
    user?.nas_permissions?.nas_order_approver ||
    nasOrderApprovers.some(name => usernameLower.includes(name) || fullNameLower.includes(name));

  // Redirect users without dashboard access to their timesheet
  useEffect(() => {
    if (user && !hasDashboardAccess) {
      navigate('/arbe/timesheet', { replace: true });
    }
  }, [user, hasDashboardAccess, navigate]);

  useEffect(() => {
    // Only fetch dashboard data for users with access
    if (user && hasDashboardAccess) {
      fetchDashboardStats();
      fetchChartData();
      fetchVendoSummary();
      fetchVendoMachinesCount();
      fetchUnsignedPayslips();
      fetchMyCashAdvance();
      
      // Fetch pending NAS orders for approvers
      if (canApproveNASOrders) {
        fetchPendingNASOrders();
      }
    }
  }, [user, hasDashboardAccess, canApproveNASOrders]);

  const fetchMyCashAdvance = async () => {
    try {
      const response = await axios.get(`${API}/cash-advance/my-requests`);
      const data = response.data || {};
      const advances = data.cash_advances || [];
      const outstanding = data.total_outstanding || advances
        .filter(ca => ca.status === 'released' && ca.amount_remaining > 0)
        .reduce((sum, ca) => sum + (ca.amount_remaining || 0), 0);
      const pending = advances
        .filter(ca => ca.status === 'pending' || ca.status === 'approved')
        .reduce((sum, ca) => sum + (ca.amount || 0), 0);
      setMyCashAdvance({ outstanding, pending, advances });
    } catch (error) {
      console.error('Error fetching cash advance:', error);
    }
  };

  const fetchPendingNASOrders = async () => {
    try {
      const response = await axios.get(`${API}/nas/customer-orders`);
      const allOrders = response.data || [];
      // Filter for pending orders only
      const pending = allOrders.filter(order => order.status === 'pending');
      setPendingNASOrders(pending);
    } catch (error) {
      console.error('Error fetching NAS orders:', error);
    }
  };

  const handleApproveOrder = async (orderId) => {
    try {
      await axios.put(`${API}/nas/customer-orders/${orderId}`, {
        status: 'approved',
        approved_by: user?.full_name || user?.username,
        approved_at: new Date().toISOString()
      });
      fetchPendingNASOrders();
      alert('Order approved successfully!');
    } catch (error) {
      console.error('Error approving order:', error);
      alert('Failed to approve order');
    }
  };

  const fetchUnsignedPayslips = async () => {
    try {
      const response = await axios.get(`${API}/payslips/unsigned-count`);
      setUnsignedPayslips(response.data);
    } catch (error) {
      console.error('Error fetching unsigned payslips:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/monthly-chart`);
      setChartData(response.data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const fetchVendoSummary = async () => {
    try {
      const response = await axios.get(`${API}/vendo-collections/summary`);
      setVendoSummary(response.data);
    } catch (error) {
      console.error('Error fetching vendo summary:', error);
    }
  };

  const fetchVendoMachinesCount = async () => {
    try {
      const response = await axios.get(`${API}/vendo-machines`);
      setVendoMachinesCount(response.data?.length || 0);
    } catch (error) {
      console.error('Error fetching vendo machines:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Install App Banner - Shows on mobile */}
      <InstallAppBanner />
      
      {/* Unsigned Payslips Alert */}
      {unsignedPayslips.count > 0 && (
        <div 
          className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 shadow-sm"
          data-testid="unsigned-payslips-alert"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileSignature className="text-amber-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-amber-800">
                  You have {unsignedPayslips.count} unsigned payslip{unsignedPayslips.count > 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-amber-600">
                  Please sign your payslip{unsignedPayslips.count > 1 ? 's' : ''} to acknowledge receipt
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/payslip-archive')}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="sign-payslips-btn"
            >
              <FileSignature size={16} className="mr-2" />
              Sign Now
            </Button>
          </div>
        </div>
      )}
      
      {/* Cash Advance Summary Card */}
      {(myCashAdvance.outstanding > 0 || myCashAdvance.pending > 0) && (
        <div 
          className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 shadow-sm"
          data-testid="cash-advance-summary"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Wallet className="text-purple-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-purple-800">
                  Cash Advance Balance
                </h3>
                <div className="flex items-center gap-4 mt-1">
                  {myCashAdvance.outstanding > 0 && (
                    <p className="text-sm">
                      <span className="text-purple-600">Outstanding: </span>
                      <span className="font-bold text-purple-800">₱{myCashAdvance.outstanding.toLocaleString()}</span>
                    </p>
                  )}
                  {myCashAdvance.pending > 0 && (
                    <p className="text-sm">
                      <span className="text-blue-600">Pending: </span>
                      <span className="font-bold text-blue-700">₱{myCashAdvance.pending.toLocaleString()}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/cash-advance')}
              variant="outline"
              className="border-purple-300 text-purple-700 hover:bg-purple-100"
              data-testid="view-ca-btn"
            >
              <Wallet size={16} className="mr-2" />
              View Details
            </Button>
          </div>
        </div>
      )}
      
      {/* Pending NAS Orders - For Order Approvers */}
      {canApproveNASOrders && pendingNASOrders.length > 0 && (
        <div 
          className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 shadow-sm"
          data-testid="pending-nas-orders"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Package className="text-green-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-green-800">
                  Pending NAS Orders ({pendingNASOrders.length})
                </h3>
                <p className="text-sm text-green-600">
                  Orders waiting for your approval
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/nas/order-approval')}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="view-nas-orders-btn"
            >
              <CheckCircle size={16} className="mr-2" />
              View All Orders
            </Button>
          </div>
          
          {/* Quick Order List */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {pendingNASOrders.slice(0, 5).map(order => (
              <div 
                key={order.id} 
                className="flex items-center justify-between bg-white rounded-lg p-3 border border-green-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Clock size={18} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{order.customer_name || 'Customer'}</p>
                    <p className="text-xs text-slate-500">
                      {order.items?.length || 0} items • ₱{(order.total_amount || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                    Pending
                  </span>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => handleApproveOrder(order.id)}
                  >
                    <CheckCircle size={14} className="mr-1" />
                    Approve
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          {pendingNASOrders.length > 5 && (
            <p className="text-sm text-green-600 text-center mt-3">
              +{pendingNASOrders.length - 5} more orders pending...
            </p>
          )}
        </div>
      )}
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          title="Total Subscribers"
          value={stats?.total_subscribers || 0}
          icon={Users}
          color="blue"
          testId="stat-total-subscribers"
        />
        <StatCard
          title="No. of Vendo"
          value={vendoMachinesCount}
          icon={Coins}
          color="purple"
          testId="stat-vendo-count"
        />
        <StatCard
          title="Subscription Income"
          value={`₱${(stats?.total_revenue || 0).toLocaleString()}`}
          icon={TrendingUp}
          color="emerald"
          testId="stat-monthly-revenue"
        />
        <StatCard
          title="Vendo Income"
          value={`₱${(vendoSummary?.total_amount || 0).toLocaleString()}`}
          icon={Coins}
          color="green"
          testId="stat-vendo-income"
        />
        <StatCard
          title="Overdue Payments"
          value={stats?.overdue_count || 0}
          icon={AlertTriangle}
          color="red"
          testId="stat-overdue-payments"
        />
      </div>

      {/* Recent Payments */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="recent-payments">
        <h3 className="text-lg font-jakarta font-bold text-slate-900 mb-4">Recent Payments</h3>
        <div className="space-y-3">
          {stats?.recent_payments && stats.recent_payments.length > 0 ? (
            stats.recent_payments.map((payment) => (
              <div
                key={payment.id}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100"
                data-testid={`recent-payment-${payment.id}`}
              >
                <div>
                  <p className="font-medium text-slate-900">{payment.subscriber_name}</p>
                  <p className="text-sm text-slate-600">{new Date(payment.payment_date).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">₱{payment.amount.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{payment.payment_method}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-slate-500 text-center py-4">No recent payments</p>
          )}
        </div>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="financial-summary">
          <h3 className="text-lg font-jakarta font-bold text-slate-900 mb-4">This Month</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Projected Income</span>
              <span className="font-bold text-blue-600">₱{(stats?.projected_income || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Subscription Income</span>
              <span className="font-bold text-green-600">₱{(stats?.total_revenue || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Vendo Income</span>
              <span className="font-bold text-purple-600">₱{(vendoSummary?.total_amount || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center border-t border-slate-100 pt-2">
              <span className="text-slate-700 font-medium">Total Income</span>
              <span className="font-bold text-emerald-600">₱{((stats?.total_revenue || 0) + (vendoSummary?.total_amount || 0)).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Expenses</span>
              <span className="font-bold text-red-600">₱{(stats?.total_expenses || 0).toLocaleString()}</span>
            </div>
            <div className="border-t border-slate-200 pt-4">
              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-900">Net Profit</span>
                <span className={`font-bold text-xl ${((stats?.net_profit || 0) + (vendoSummary?.total_amount || 0)) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  ₱{((stats?.net_profit || 0) + (vendoSummary?.total_amount || 0)).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Monthly Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="monthly-chart">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="text-blue-600" size={24} />
            <h3 className="text-lg font-jakarta font-bold text-slate-900">Monthly Financial Overview</h3>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(value) => `₱${(value / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="projected" name="Projected Income" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="collected" name="Collected Income" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                <Bar dataKey="profit" name="Profit" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;